export type CustomDomainProduct = 'confluence' | 'jira';

export type CustomDomain = {
	product: CustomDomainProduct;
	hostName?: string | null;
};

/**
 * Rewrites an Atlassian cloud URL to the matching product custom-domain host when available.
 *
 * Only the hostname is substituted — the path (including the `/wiki` or `/jira`
 * product prefix) is preserved exactly, matching Atlassian's custom-domain
 * routing behaviour:
 *   `starbucks.atlassian.net/wiki/spaces/ENG/pages/1`
 *     → `confluence.atlassian.starbucks.com/wiki/spaces/ENG/pages/1`
 *
 * The AGG `cloudUrl` is always a bare root (e.g. `https://starbucks.atlassian.net`,
 * pathname `/`). Handlers append the product path themselves (e.g. `/wiki/spaces/...`),
 * so this function must not append any path prefix — doing so would produce
 * double-path URLs like `/wiki/wiki/spaces/...`.
 *
 * Returns `base` unchanged when no custom domain matches the requested product
 * or when URL parsing fails.
 */
export const rewriteBaseToCustomDomain = (
	base: string,
	product: CustomDomainProduct,
	customDomains?: CustomDomain[] | null,
): string => {
	const match = customDomains?.find(
		(domain): domain is CustomDomain & { hostName: string } =>
			domain.product === product && Boolean(domain.hostName),
	);

	if (!match) {
		return base;
	}

	try {
		const url = new URL(base);
		url.hostname = match.hostName;
		// url.toString() normalises bare-root URLs to include a trailing slash
		// (e.g. 'https://wiki.starbucks.com/'). All handlers concatenate a
		// leading-slash path, so we strip the trailing slash to avoid double-slashes
		// (e.g. 'https://wiki.starbucks.com//wiki/spaces/...' → correct).
		return url.toString().replace(/\/$/, '');
	} catch {
		return base;
	}
};
